import React, { useEffect, useState } from 'react';
import '../styles/VerifyProperty.css';
import { Header, Footer, Input, Loading, PropertyIcon, Switch, ReportLoadingAnimation, Modal } from '../components';
import { ADU, Bathroom, Bedroom, SquareFootage, YearBuilt } from '../assets/icons';
import { colour, numberFormatter } from '../styles/GlobalStyles';
import { Button, Dots } from '../components';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkOnAuthStateChanged, cloudFunctionV2, propertyGatewayString, propertyTypeFormat, setSubData, sendErrorEmail, setDataRandomId, recordEvent, getSubDocument } from '../functions';
import { NoMatchModal } from '../modalbody';
import { propertyDataReAPI } from '../defaults';
import { CarParking01Icon, PoolIcon } from 'hugeicons-react';

function VerifyProperty() {
    const [loggedIn, setLoggedIn] = useState(null);
    const [userId, setUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [adu, setAdu] = useState(false);
    const [pool, setPool] = useState(false);
    const [garage, setGarage] = useState(false);
    const [zillowUrl, setZillowUrl] = useState("");

    useEffect(() => {
        document.title = "Verify Property | Clozers";

        const fetchUserData = async() => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);
                setUserId(user.userId);
            }
            else {
                setLoggedIn(false);
            }
        };
        
        fetchUserData();

    }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state;
    const property = state === null ? null : state.data;
    const companyId = state === null ? null : state.companyId;
    const [bedrooms, setBedrooms] = useState(property === null ? 0 : property.propertyInfo.bedrooms === null ? 0 : property.propertyInfo.bedrooms);
    const [bathrooms, setBathrooms] = useState(property === null ? 0 : property.propertyInfo.bathrooms === null ? 0 : property.propertyInfo.bathrooms);
    const [squareFootage, setSquareFootage] = useState(property === null ? 0 : property.propertyInfo.livingSquareFeet === null ? 0 : property.propertyInfo.livingSquareFeet);
    const [yearBuilt, setYearBuilt] = useState(property === null ? 0 : property.propertyInfo.yearBuilt === null ? 0 : property.propertyInfo.yearBuilt);
    const [newValue, setNewValue] = useState("");
    const [editValue, setEditValue] = useState(bedrooms === 0 ? 2 : bathrooms === 0 ? 3 : squareFootage === 0 ? 4 : yearBuilt === 0 ? 5 : null);
    const [sqFootageWarning, setSqFootageWarning] = useState(null);
    const [wrongMarket, setWrongMarket] = useState(false);
    const [noMatchModal, setNoMatchModal] = useState(false);
    const [externalCompanyNoMatch, setExternalCompanyNoMatch] = useState(false);
    const [propertyInfoCompare, setPropertyInfoCompare] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [propertyNotes, setPropertyNotes] = useState("");
    const origin = window.location.origin;
    const testMode = origin.includes("localhost");

    useEffect(() => {
        if ( state === null ) {
            navigate("/");
        }
    }, [navigate, state]);

    const details = property === null ? [] : [
        {
            label: "Address",
            subLabel: null,
            value: property.propertyInfo.address.label,
            icon: null,
            edit: false,
            input: true,
            editable: false
        },
        {
            label: "Property type",
            subLabel: null,
            value: propertyTypeFormat(property.propertyType),
            icon: <PropertyIcon propertyType={property.propertyType} fill={colour.gray07} size={16} className="verify-property-icon" />,
            edit: false,
            input: true,
            editable: false
        },
        {
            label: "Beds",
            subLabel: null,
            value: `${bedrooms} bed`,
            rawValue: bedrooms,
            icon: <Bedroom fill={colour.gray07} size={16} className="verify-property-icon" />,
            edit: editValue === 2 ? true : false,
            input: true,
            editable: true
        },
        {
            label: "Baths",
            subLabel: null,
            value: `${bathrooms} bath`,
            rawValue: bathrooms,
            icon: <Bathroom fill={colour.gray07} size={16} className="verify-property-icon" />,
            edit: editValue === 3 ? true : false,
            input: true,
            editable: true
        },
        {
            label: "Living square footage",
            subLabel: null,
            value: `${numberFormatter.format(squareFootage)} sqft`,
            rawValue: squareFootage,
            icon: <SquareFootage fill={colour.gray07} className="verify-property-icon" />,
            edit: editValue === 4 ? true : false,
            input: true,
            editable: true
        },
        {
            label: "Year built",
            subLabel: null,
            value: yearBuilt,
            rawValue: yearBuilt,
            icon: <YearBuilt fill={colour.gray07} className="verify-property-icon" />,
            edit: editValue === 5 ? true : false,
            input: true,
            editable: true
        },
        {
            label: "Accessory Dwelling Unit (ADU)",
            subLabel: null,
            switchLabel: "Subject property has an ADU",
            value: adu,
            rawValue: adu,
            icon: <ADU fill={colour.gray07} className="verify-property-icon" />,
            edit: false,
            input: false,
            editable: false
        },
        {
            label: "Pool",
            subLabel: null,
            switchLabel: "Subject property has a pool",
            value: pool,
            rawValue: pool,
            icon: <PoolIcon color={colour.gray07} size={16} className="verify-property-icon" />,
            edit: false,
            input: false,
            editable: false
        },
        {
            label: "Garage",
            subLabel: null,
            switchLabel: "Subject property has a garage",
            value: garage,
            rawValue: garage,
            icon: <CarParking01Icon color={colour.gray07} size={16} className="verify-property-icon" />,
            edit: false,
            input: false,
            editable: false
        }
    ];

    const toggleEdit = (index) => {
        if ( editValue === index ) {
            setEditValue(null);
            setNewValue("");
        }
        else {
            setNewValue(index === 2 ? bedrooms : index === 3 ? bathrooms : index === 4 ? squareFootage : yearBuilt);
            setEditValue(index);
        }
    };

    const onChangeValue = (text) => {
        if ( editValue === 5 && text.length > 4 ) {
            return;
        }
        setNewValue(text);
    };

    const onChangePropertyNotes = (text) => {
        setPropertyNotes(text);
    };

    const saveEdit = (index) => {
        if ( index === 2 ) {
            setBedrooms(newValue);
        }
        else if ( index === 3 ) {
            setBathrooms(newValue);
        }
        else if ( index === 4 ) {
            setSquareFootage(newValue);
            if ( Number(newValue) > 4000 ) {
                setSqFootageWarning(`FYI - this is NOT for lot size! Please double check ${numberFormatter.format(Number(newValue))} is only for the living area!`);
            }
            else {
                setSqFootageWarning(null);
            }
        }
        else if ( index === 5 ) {
            setYearBuilt(newValue);
        }
        setEditValue(null);
        setNewValue("");
    };

    const onChangeZillowURL = (value) => {
        setZillowUrl(value);
    };

    const checkIfPropertySaved = async() => {
        const colRef = "companies";
        const docRef = companyId;
        const subColRef = "properties";
        const id = `${property.id}`;

        return new Promise(async(resolve) => {
            const queryData = await getSubDocument(colRef, docRef, subColRef, id);
            if ( queryData.status === 200 ) {
                const res = {
                    status: 200,
                    data: queryData.data.data
                }
                resolve(res);
            }
            else {
                const res = {
                    status: 400
                }
                resolve(res);
            }
        });
    };

    const cta = async(reRun) => {
        if ( isNaN(Number(bedrooms)) || isNaN(Number(bathrooms)) || isNaN(Number(squareFootage)) || isNaN(Number(yearBuilt)) ) {
            setError("Please enter a valid number");
            return;
        }

        property.propertyInfo.bedrooms = Number(bedrooms);
        property.propertyInfo.bathrooms = Number(bathrooms);
        property.propertyInfo.livingSquareFeet = Number(squareFootage);
        property.propertyInfo.yearBuilt = Number(yearBuilt);
        setError(null);

        if ( property.propertyInfo.bedrooms === null || property.propertyInfo.bedrooms === 0 ) {
            setError("Please enter the number of bedrooms");
            return;
        }
        else if ( property.propertyInfo.bathrooms === null || property.propertyInfo.bathrooms === 0 ) {
            setError("Please enter the number of bathrooms");
            return;
        }
        else if ( property.propertyInfo.livingSquareFeet === null || property.propertyInfo.livingSquareFeet === 0 ) {
            setError("Please enter the square footage of the property");
            return;
        }
        else if ( property.propertyInfo.yearBuilt === null || property.propertyInfo.yearBuilt === 0 || property.propertyInfo.yearBuilt === "" ) {
            setError("Please enter the year the property was built");
            return;
        }

        if ( 
            editValue !== null &&
            ((editValue === 2 && newValue !== property.propertyInfo.bedrooms) ||
            (editValue === 3 && newValue !== property.propertyInfo.bathrooms) ||
            (editValue === 4 && newValue !== property.propertyInfo.livingSquareFeet) ||
            (editValue === 5 && newValue !== property.propertyInfo.yearBuilt))
        ) {
            setError("Please save your changes");
            return;
        }

        recordEvent("property_verified", {});

        const url = process.env.REACT_APP_AWS_QUERY_URL;
        const params = {
            propertyId: property.id,
            userId: userId,
            propertyAddress: property.propertyInfo.address.label,
            countyId: property.propertyInfo.address.fips,
            propertyType: property.propertyType,
            beds: property.propertyInfo.bedrooms,
            baths: property.propertyInfo.bathrooms,
            livingSquareFootage: property.propertyInfo.livingSquareFeet,
            yearBuilt: property.propertyInfo.yearBuilt,
            mortgageOwed: 0,
            additionalDebt: 0,
            miscSellerInfoDescription: "",
            buildingRedFlags: "",
            rehabMin: 0,
            rehabMax: 0,
            otherExpenses: 0,
            adu: adu,
            pool: pool,
            garage: garage,
            propertyNotes: propertyNotes,
            zillowUrl: zillowUrl
        };

        const defaultPropertyData = await propertyDataReAPI.property({
            property: property,
            userId: userId,
            propertyNotes: propertyNotes,
            adu: adu,
            pool: pool,
            garage: garage
        });

        const checkSaved = await checkIfPropertySaved();
        if ( checkSaved.status === 200 ) {
            if ( checkSaved.data.bedrooms === undefined || reRun === false ) {
                navigate(`/run-comps/${params.countyId}/${params.propertyId}`);
                return;
            }
            else if ( reRun !== true && checkSaved.data.bedrooms === params.beds && checkSaved.data.bathrooms === params.baths && checkSaved.data.livingSquareFeet === params.livingSquareFootage && checkSaved.data.yearBuilt === params.yearBuilt && checkSaved.data.adu === params.adu ) {
                navigate(`/run-comps/${params.countyId}/${params.propertyId}`);
                return;
            }
            else if ( reRun === null && (checkSaved.data.bedrooms !== params.beds || checkSaved.data.bathrooms !== params.baths || checkSaved.data.livingSquareFeet !== params.livingSquareFootage || checkSaved.data.yearBuilt !== params.yearBuilt) ) {
                setNoMatchModal(true);
                setLoading(false);
                setExternalCompanyNoMatch(false);
                setPropertyInfoCompare({
                    old: [
                        {
                            label: "Beds",
                            value: `${checkSaved.data.bedrooms} bed`,
                            rawValue: checkSaved.data.bedrooms
                        },
                        {
                            label: "Baths",
                            value: `${checkSaved.data.bathrooms} bath`,
                            rawValue: checkSaved.data.bathrooms
                        },
                        {
                            label: "Square footage",
                            value: `${numberFormatter.format(checkSaved.data.livingSquareFeet)} sqft`,
                            rawValue: checkSaved.data.livingSquareFeet
                        },
                        {
                            label: "Year built",
                            value: checkSaved.data.yearBuilt,
                            rawValue: checkSaved.data.yearBuilt
                        },
                        {
                            label: "ADU",
                            value: checkSaved.data.adu ? "ADU" : "No ADU",
                            rawValue: checkSaved.data.adu ? true : false
                        },
                        {
                            label: "Pool",
                            value: checkSaved.data.pool ? "Pool" : "No Pool",
                            rawValue: checkSaved.data.pool ? true : false
                        },
                        {
                            label: "Garage",
                            value: checkSaved.data.garage ? "Garage" : "No Garage",
                            rawValue: checkSaved.data.garage ? true : false
                        }
                    ],
                    new: [
                        {
                            label: "Beds",
                            value: `${params.beds} bed`,
                            rawValue: params.beds
                        },
                        {
                            label: "Baths",
                            value: `${params.baths} bath`,
                            rawValue: params.baths
                        },
                        {
                            label: "Square footage",
                            value: `${numberFormatter.format(params.livingSquareFootage)} sqft`,
                            rawValue: params.livingSquareFootage
                        },
                        {
                            label: "Year built",
                            value: params.yearBuilt,
                            rawValue: params.yearBuilt
                        },
                        {
                            label: "ADU",
                            value: params.adu ? "ADU" : "No ADU",
                            rawValue: params.adu ? true : false
                        },
                        {
                            label: "Pool",
                            value: params.pool ? "Pool" : "No Pool",
                            rawValue: params.pool ? true : false
                        },
                        {
                            label: "Garage",
                            value: params.garage ? "Garage" : "No Garage",
                            rawValue: params.garage ? true : false
                        }
                    ]
                })
                return;
            }
        }
        
        setLoading(true);
        const resourceId = "propertyDetailV2";
        const getGatewayQueryString = await propertyGatewayString("propertyDetailv2?", params);
        const getGatewayParams = {
            type: "gateway",
            resourceId: resourceId,
            queryString: `${getGatewayQueryString}${reRun === true ? "&forceRun=true" : ""}`
        };

        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }, 50);

        const queryData = await cloudFunctionV2(url, getGatewayParams);
        if ( queryData.status === 200 ) {
            const body = queryData.body;
            if ( body !== undefined && body !== null && body.error !== undefined ) {
                setLoading(false);
                if ( body.error.includes("Invalid county") ) {
                    setWrongMarket(true);
                    recordEvent("wrong_market", {});
                }
                else {
                    setWrongMarket(false);
                }
                return;
            }
            else {
                setWrongMarket(false);
            }

            // Check if the property has the same beds and baths as what the user entered
            const returnedProperty = body.data === undefined && body.propertyDetails === undefined ? null : body.data === undefined ? body.propertyDetails : body.data.propertyDetails;
            if ( returnedProperty !== null && reRun === null && (returnedProperty.propertyAttributes.bedrooms !== params.beds || returnedProperty.propertyAttributes.bathrooms !== params.baths || returnedProperty.propertyAttributes.livingSquareFootage !== params.livingSquareFootage || returnedProperty.propertyAttributes.yearBuilt !== params.yearBuilt) ) {
                setNoMatchModal(true);
                setLoading(false);
                setExternalCompanyNoMatch(true);
                setPropertyInfoCompare({
                    old: [
                        {
                            label: "Beds",
                            value: `${returnedProperty.propertyAttributes.bedrooms} bed`,
                            rawValue: returnedProperty.propertyAttributes.bedrooms
                        },
                        {
                            label: "Baths",
                            value: `${returnedProperty.propertyAttributes.bathrooms} bath`,
                            rawValue: returnedProperty.propertyAttributes.bathrooms
                        },
                        {
                            label: "Square footage",
                            value: `${numberFormatter.format(returnedProperty.propertyAttributes.livingSquareFootage)} sqft`,
                            rawValue: returnedProperty.propertyAttributes.livingSquareFootage
                        },
                        {
                            label: "Year built",
                            value: returnedProperty.propertyAttributes.yearBuilt,
                            rawValue: returnedProperty.propertyAttributes.yearBuilt
                        }
                    ],
                    new: [
                        {
                            label: "Beds",
                            value: `${params.beds} bed`,
                            rawValue: params.beds
                        },
                        {
                            label: "Baths",
                            value: `${params.baths} bath`,
                            rawValue: params.baths
                        },
                        {
                            label: "Square footage",
                            value: `${numberFormatter.format(params.livingSquareFootage)} sqft`,
                            rawValue: params.livingSquareFootage
                        },
                        {
                            label: "Year built",
                            value: params.yearBuilt,
                            rawValue: params.yearBuilt
                        }
                    ]
                })
                return;
            }

            if ( reRun === false && externalCompanyNoMatch === false ) {
                // Property has already been ran by the same company for a credit
                // and is not being re-run
            }
            else {
                // Remove 1 credit for the search
                cloudFunctionV2(process.env.REACT_APP_REMOVE_CREDIT, {companyId: companyId});
            }

            const colRef = "companies";
            const subColRef = "properties";
            const id = `${property.id}`;
            await setSubData(colRef, companyId, subColRef, id, defaultPropertyData);
            
            defaultPropertyData.companyId = companyId;
            delete defaultPropertyData.loaded;
            await setDataRandomId("recent_searches", defaultPropertyData);

            navigate(`/run-comps/${params.countyId}/${params.propertyId}`);
            setError(null);
        }
        else if ( queryData.status === 405 ) {
            setError("There was an error with the request, please contact us for more details");
            await sendErrorEmail({
                userId: userId,
                address: property.propertyInfo.address.label,
                notes: "Verify property page",
                propertyId: property.id
            });
            setLoading(false);
        }
        else {
            console.log(queryData);
            if ( queryData.body && queryData.body.error ) {
                setError(queryData.body.error);
            }
            else if ( queryData.error && typeof queryData.error === "string" ) {
                setError(queryData.error);
            }
            else {
                setError("There was an error with the request, please try again");
            }
            setLoading(false);
        }
        setExternalCompanyNoMatch(false);
    };

    const backHome = () => {
        recordEvent("back_to_home", {});
        navigate("/");
    };

    const closeNoMatchModal = () => {
        setNoMatchModal(false);
        setExternalCompanyNoMatch(false);
        setSelectedVersion(null);
    };

    // console.log(property.propertyInfo.construction);

    return (
        <div className={"verify-property-outer-container " + (loading === true ? "bg-colour-black" : "")}>
            <Header
                loggedIn={loggedIn}
                showTimeline={loading}
            />
            {
                loading === true ?
                <div className="verify-property-inner-container relative-container">
                    <ReportLoadingAnimation />
                </div>
                :
                wrongMarket === true ?
                <div className="verify-property-inner-container relative-container">
                    <div className="verify-property-title-container">
                        <h1 className="heading-large-bold colour-primary text-align-center">
                            We're not active in this market yet
                        </h1>
                        <p className="body-regular colour-primary text-align-center">
                            We only have access to Tampa Bay, FL, St Louis, MO and Maricopa County, AZ right now. 
                            If you need more counties,
                            send us an email at <a href="mailto:info@clozers.co" className="body-bold colour-primary">info@clozers.co</a> and we'll see what we can do!
                        </p>
                    </div>
                    <div className="verify-property-wrong-market-container">
                        <Button
                            buttonType="primary-button"
                            size="large"
                            text={"Back to home"}
                            cta={() => backHome()}
                            disabled={false}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                    </div>
                </div>
                :
                <div className="verify-property-inner-container relative-container">
                    {
                        loading === true &&
                        <Loading />
                    }
                    {
                        noMatchModal === true &&
                        <Modal
                            heading="Conflicting property info"
                            subheading={null}
                            body={<NoMatchModal propertyInfoCompare={propertyInfoCompare} externalCompanyNoMatch={externalCompanyNoMatch} selectedOption={selectedVersion} setSelectedOption={setSelectedVersion} setBedrooms={setBedrooms} setBathrooms={setBathrooms} setSquareFootage={setSquareFootage} setYearBuilt={setYearBuilt} />}
                            type="basic"
                            cancelText={"Close"}
                            cancelButtonCTA={closeNoMatchModal}
                            actionButtonText={"Confirm"}
                            actionButtonCTA={() => cta(selectedVersion === 0 ? false : selectedVersion === 1 ? true : null)}
                            secondaryButtonText={null}
                            secondaryButtonCTA={null}
                            exitButtonCTA={closeNoMatchModal}
                            loading={false}
                            actionButtonDisabled={selectedVersion === null ? true : false}
                            leftIcon={null}
                            rightIcon={null}
                            cancelLeftIcon={null}
                            cancelRightIcon={null}
                            customClass="verify-property-no-match-modal"
                        />
                    }
                    {
                        noMatchModal === true &&
                        <div className="dark-bg">
                        </div>
                    }
                    <Dots
                        limit={3}
                        selectedFill={colour.grayScaleGray03}
                        selectedStroke=""
                        unselectedFill={colour.grayScaleGray01}
                        unselectedStroke=""
                        selectedIndex={0}
                        className=""
                    />
                    <div className="verify-property-title-container">
                        <h1 className="heading-large-bold colour-primary text-align-center">
                            Verify basic info
                        </h1>
                        <span className="body-regular colour-secondary block-text text-align-center">
                            Please verify that our data for this property is correct.
                        </span>
                        {
                            testMode === true && property !== null &&
                            <span className="body-regular colour-primary">
                                {property.id}
                            </span>
                        }
                    </div>
                    <div className="verify-property-details-container">
                        {
                            details.map((item, index) =>
                                item.input === true &&
                                <React.Fragment key={index}>
                                    <div className="verify-property-details-element">
                                        <div className="verify-property-details-element-left-container">
                                            <div className="verify-property-details-label-container">
                                                {item.icon}
                                                <span className="body-bold colour-primary">
                                                    {item.label}
                                                </span>
                                            </div>
                                            {
                                                item.subLabel !== null &&
                                                <span className="body-regular colour-secondary">
                                                    Optionally add a link to help us improve results. Please <a href="https://www.google.com" target="_blank" rel="noreferrer" className="colour-primary underline">follow these instructions</a> to get the right link.
                                                </span>
                                            }
                                            {
                                                item.label.includes("Zillow") ?
                                                <Input
                                                    label={null}
                                                    labelType={null}
                                                    info={false}
                                                    value={item.value}
                                                    onChange={(text) => onChangeZillowURL(text.target.value)}
                                                    placeholder={"Paste your link here"}
                                                    type="text"
                                                    leftUnit={null}
                                                    rightUnit={null}
                                                    unitSwitch={false}
                                                    leftUnitSelected={true}
                                                    unitSwitchFunc={null}
                                                    disabled={false}
                                                    multiline={false}
                                                    error={false}
                                                    onMouseEnter={null}
                                                    onMouseLeave={null}
                                                    reset={false}
                                                    resetFunc={null}
                                                    resetPosition={null}
                                                    focusMultiline={false}
                                                    prompt={false}
                                                />
                                                :
                                                item.edit === false ?
                                                <span className="body-regular colour-primary">
                                                    {item.value}
                                                </span>
                                                :
                                                <div className="verify-property-details-edit-container">
                                                    <Input
                                                        label={null}
                                                        labelType={null}
                                                        info={false}
                                                        value={newValue}
                                                        onChange={(text) => onChangeValue(text.target.value)}
                                                        placeholder={item.rawValue}
                                                        type="text"
                                                        leftUnit={null}
                                                        rightUnit={null}
                                                        unitSwitch={false}
                                                        leftUnitSelected={true}
                                                        unitSwitchFunc={null}
                                                        disabled={false}
                                                        multiline={false}
                                                        error={false}
                                                        onMouseEnter={null}
                                                        onMouseLeave={null}
                                                        reset={false}
                                                        resetFunc={null}
                                                        resetPosition={null}
                                                        focusMultiline={false}
                                                        prompt={false}
                                                    />
                                                    <Button
                                                        buttonType="primary-button"
                                                        size="medium"
                                                        text={"Save"}
                                                        cta={() => saveEdit(index)}
                                                        disabled={item.rawValue === newValue ? true : newValue === "" ? true : false}
                                                        leftIcon={null}
                                                        leftIconClass=""
                                                        rightIcon={null}
                                                        rightIconClass=""
                                                        alt=""
                                                        standardIcon={false}
                                                        badge={false}
                                                        badgeVal={null}
                                                        badgeBG={null}
                                                        buttonRef={null}
                                                        spinner={false}
                                                    />
                                                </div>
                                            }
                                            {
                                                index === 4 && sqFootageWarning !== null &&
                                                <span className="body-regular colour-error">
                                                    {sqFootageWarning}
                                                </span>
                                            }
                                        </div>
                                        {
                                            item.editable === true &&
                                            <div 
                                                className="verify-property-details-element-right-container"
                                                onClick={() => toggleEdit(index)}
                                            >
                                                <span className="body-regular underline text-link cursor-pointer">
                                                    {item.edit === true ? "Cancel" : "Edit"}
                                                </span>
                                            </div>
                                        }
                                    </div>
                                    <div className="light-grey-divider">
                                    </div>
                                </React.Fragment>
                            )
                        }
                        {
                            details.map((item, index) => 
                                item.input === false &&
                                <div 
                                    className="verify-property-details-switch-element"
                                    key={index}
                                >
                                    <div className="verify-property-details-switch-title">
                                        {item.icon}
                                        <span className="body-bold colour-primary">
                                            {item.label}
                                        </span>
                                    </div>
                                    <Switch
                                        label={item.switchLabel}
                                        val={index === 6 ? adu : index === 7 ? pool : index === 8 ? garage : null}
                                        setVal={index === 6 ? setAdu : index === 7 ? setPool : index === 8 ? setGarage : null}
                                    />
                                </div>
                            )
                        }
                        <div className="verify-property-details-element">
                            <Input
                                label={"Unique insights?"}
                                labelType={"default"}
                                info={false}
                                value={propertyNotes}
                                onChange={(text) => onChangePropertyNotes(text.target.value)}
                                placeholder={"This area/property is difficult to comp. You have to watch out for XYZ..."}
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={true}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                        </div>
                    </div>
                    <div className="verify-property-button-container">
                        <Button
                            buttonType="primary-button"
                            size="large"
                            text={"Next"}
                            cta={() => cta(null)}
                            disabled={loading}
                            leftIcon={null}
                            leftIconClass=""
                            rightIcon={null}
                            rightIconClass=""
                            alt=""
                            standardIcon={false}
                            badge={false}
                            badgeVal={null}
                            badgeBG={null}
                            buttonRef={null}
                            spinner={false}
                        />
                    </div>
                    {
                        error !== null &&
                        <div className="error-message-container">
                            <span className="body-regular colour-error">
                                {error}
                            </span>
                        </div>
                    }
                </div>
            }
            {
                loading === false &&
                <Footer />
            }
        </div>
    )
};

export default VerifyProperty;